import {
  HoldReaction,
  HoldRestrictionType,
  HoldStatus,
  HoldTypeRestrictionDto,
  JourneyCategory,
} from '@planning/app/api'
import { ContainerJourneyStatus } from '@planning/components/molecules/ContainerJourneyStatusChip'
import { formatDate } from '@tom-ui/utils'
import dayjs from 'dayjs'
import { IHoldItem } from '../../../rt-stores/hold/HoldItem'

// Table model combining Hold and ContainerJourney info for UI
export interface HoldsTableData {
  id: number
  journeyId: number
  holdStatus: HoldStatus
  type: string
  createdRaw?: string
  created?: string
  age?: string
  ageInHours?: number
  containerNumber: string
  restrictions?: HoldTypeRestrictionDto[] | null
  journeyStatus: ContainerJourneyStatus
  category?: JourneyCategory
  cargoStatus?: 'Full' | 'Empty'
  isoCode?: string | null
  operator: string
}

// Helper to map domain objects to table model
export function mapToTableData(hold: IHoldItem): HoldsTableData {
  console.log('mapToTableData ', hold)
  return {
    id: hold.id,
    journeyId: hold?.data?.containerJourneyId,
    holdStatus: hold?.data?.status,
    type: hold?.data?.holdType?.name ?? '',
    createdRaw: hold?.data?.created,
    created: hold?.data?.created ? formatDate(dayjs(hold.data.created)) : undefined,
    age: calculateAge(hold?.data?.created),
    ageInHours: hold?.data?.created
      ? Math.floor(dayjs().diff(dayjs(hold.data.created), 'minute') / 60)
      : undefined,
    containerNumber: hold?.container?.data?.number ?? '',
    restrictions: mapRestrictions(hold?.data?.holdType?.restrictions),
    journeyStatus: hold?.containerJourney?.status ?? 'Unknown',
    category: hold?.containerJourney?.data?.category,
    cargoStatus: getCargoStatus(hold?.container?.data?.isEmpty),
    isoCode: hold?.container?.data?.isoCode ?? '',
    operator: hold?.container?.data?.operator ?? '',
  }
}

function mapRestrictions(
  restrictions?: { [key: string]: HoldReaction } | HoldTypeRestrictionDto[] | null,
): HoldTypeRestrictionDto[] | null {
  console.log('mapRestrictions', restrictions)
  if (!restrictions) return null

  if (Array.isArray(restrictions)) {
    return restrictions
  }

  return Object.entries(restrictions).map(([key, value]) => ({
    restrictionType: key as HoldRestrictionType,
    reaction: value,
  }))
}

const calculateAge = (created?: string) => {
  if (!created) return undefined

  const createdDate = dayjs(created)
  if (!createdDate.isValid()) return undefined

  const totalMinutes = Math.max(dayjs().diff(createdDate, 'minute'), 0)
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60

  if (hours >= 24 * 365) {
    const years = Math.round(hours / (24 * 365))
    return `${years}yr${years > 1 ? 's' : ''}`
  }

  if (hours >= 24 * 28) {
    const months = Math.round(hours / (24 * 28))
    return `${months}mo${months > 1 ? 's' : ''}`
  }

  if (hours >= 24 * 7) {
    const weeks = Math.round(hours / (24 * 7))
    return `${weeks}wk${weeks > 1 ? 's' : ''}`
  }

  if (hours >= 72) {
    const days = Math.round(hours / 24)
    return `${days}d`
  }

  if (hours > 0) {
    return `${hours}h${minutes}mins`
  }

  return `${minutes}mins`
}

const getCargoStatus = (isEmpty?: boolean) => {
  if (isEmpty !== undefined) {
    return isEmpty ? 'Empty' : 'Full'
  }
}
